import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Accordion, AccordionItem, Selection } from '@heroui/react';
import { UserInfo } from '@/widgets/UserInfo';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import { getMyProfile, isProfileStaff } from '@/entities/Profile';
import { useGetCertificatesQuery } from '@/entities/Certificates';
import { useGetAttendanceQuery } from '@/entities/Attendance';
import { useGetStudentBadgesQuery } from '@/entities/Badges';
import { usePageTitle } from '@/shared/lib/hooks';
import { Achievements, Certificates, MyAttendance, MyRequests, TeacherRequests } from '../../components';

const validTabs = ['certificates', 'attendance', 'requests', 'achievements'];
const validStaffTabs = ['requests'];

export const ProfileView = () => {
  usePageTitle('My Profile');
  const user = useSelector(getMyProfile);
  const isStaff = isProfileStaff(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: certificatesData, isLoading: isCertificatesLoading } = useGetCertificatesQuery(undefined, {
    skip: isStaff,
  });
  const { data: attendanceData, isLoading: isAttendanceLoading } = useGetAttendanceQuery(undefined, {
    skip: isStaff,
  });
  const { data: badgesData, isLoading: isBadgesLoading } = useGetStudentBadgesQuery(undefined, {
    skip: isStaff,
  });
  const certificates = certificatesData?.certificates || [];
  const certificatesCount = certificates.length;
  const badges = badgesData?.items || [];

  const tabFromUrl = searchParams.get('tab');
  const availableTabs = isStaff ? validStaffTabs : validTabs;
  const fallbackTab = isStaff ? 'requests' : 'certificates';
  const defaultTab = tabFromUrl && availableTabs.includes(tabFromUrl) ? tabFromUrl : fallbackTab;

  const defaultExpandedKeys = useMemo(() => new Set([defaultTab]), [defaultTab]);

  const handleSelectionChange = (keys: Selection) => {
    const selectedKeys = Array.from(keys) as string[];
    const newParams = new URLSearchParams(searchParams);

    if (selectedKeys.length > 0) {
      const firstKey = selectedKeys[0];
      if (availableTabs.includes(firstKey)) {
        newParams.set('tab', firstKey);
        setSearchParams(newParams, { replace: true });
      }
    } else {
      // Если аккордеон закрыт, удаляем параметр tab
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  };
  const isLoading = isStaff ? false : isCertificatesLoading || isAttendanceLoading || isBadgesLoading;
  const accordionItems = isStaff
    ? [
      <AccordionItem key="requests" textValue="My requests" title="My requests">
        <TeacherRequests />
      </AccordionItem>,
    ]
    : [
      <AccordionItem
        key="certificates"
        textValue="Certificates"
        title={
          <span className="text-[32px] font-medium">Certificates {certificatesCount > 0 && (
            <span className="ml-1 text-base text-grey-secondary font-normal">{certificatesCount}</span>
          )}</span>
        }
      >
        <Certificates data={certificates} />
      </AccordionItem>,
      <AccordionItem
        key="attendance"
        textValue="My Attendance"
        title="My Attendance"
      >
        <MyAttendance data={attendanceData} />
      </AccordionItem>,
      <AccordionItem
        key="requests"
        textValue="My Requests"
        title="My Requests"
      >
        <MyRequests />
      </AccordionItem>,
      <AccordionItem
        key="achievements"
        textValue="Achievements"
        title="Achievements"
      >
        <Achievements data={badges} />
      </AccordionItem>,
    ];

  return (
    <section className="grid grid-cols-[1fr_346px] gap-6 w-full">
      <ContentWrapper
        isLoading={isLoading}
        className="flex flex-col gap-6 min-w-0"
      >
        <Accordion
          defaultExpandedKeys={defaultExpandedKeys}
          onSelectionChange={handleSelectionChange}
          variant="splitted"
          className="gap-6 flex flex-col"
          itemClasses={{
            base: 'bg-white rounded-3xl p-6 border-0 shadow-none',
            title: 'text-[32px] font-medium',
            trigger: 'px-0 py-0 border-0 cursor-pointer',
            content: 'px-0 pt-6 border-0',
            heading: 'border-0',
            indicator: 'w-6 h-6 text-xl'
          }}
        >
          {accordionItems}
        </Accordion>
      </ContentWrapper>
      <UserInfo withInvoiceBalance />
    </section>
  );
};
